/**
 * useOutboundPauseSource — determines whether an outbound channel's current
 * pause was set by the system (the circuit breaker) or by a user.
 *
 * The backend no longer stamps a disabled-reason property (see P12): the
 * ONLY signal left for "was this pause automatic" is who wrote the
 * `ahoy_outbound_enabled` toggle event that actually produced the current
 * pause. Picking the first `off` event found in page order is wrong: a
 * later `on` can supersede an earlier `off`, and attributing the current
 * pause to that superseded `off` blames the wrong actor. This hook instead
 * finds `latestToggleEvent` — the toggle event (on OR off) with the latest
 * `occurred_at` across the loaded pages (only while the channel is actually
 * paused) — and, only when that latest event's value is `'off'`, reads its
 * `source_type_name`: `'ahoy_system'` means the circuit breaker paused it,
 * anything else means a person did.
 *
 * Returns `undefined` while loading, on error, when no toggle event is found
 * in the loaded pages, or when the latest toggle event is `'on'` (a current
 * pause with no attributable `off` event loaded) — callers treat that the
 * same as "not a system pause" (no special notice; the inline Outbound
 * property row is always the way back on regardless).
 */

import { useMemo } from 'react';

import type { CrmObject } from '@/types/crm';

import type {
  PropertyUpdateEvent,
  PropertyUpdatePayload,
  TimelineFilters,
} from '../timeline/types';
import { useTimelineViewState } from '../timeline/useTimelineViewState';

/** Mirrors the CREATION_EVENT_FILTER shape at sections/record-meta-section.tsx:37-43. */
const PAUSE_EVENT_FILTER: TimelineFilters = {
  eventTypes: ['ahoy_property_updated'],
  startDate: null,
  endDate: null,
  actorId: null,
  includeAssociated: false,
};

export function useOutboundPauseSource(
  object: CrmObject | null,
  { paused }: { paused: boolean }
): 'system' | 'user' | undefined {
  const { data, isLoading, isError } = useTimelineViewState({
    entityId: object?.entity_id ?? '',
    orgId: object ? String(object.org_id) : '',
    entityType: object?.entity_type_name,
    filters: PAUSE_EVENT_FILTER,
    limit: 50,
    enabled: !!object && paused,
  });

  return useMemo(() => {
    if (isLoading || isError) {
      return undefined;
    }
    // Find the toggle event (on OR off) with the LATEST occurred_at across
    // the loaded pages -- never just the first `off` encountered in page
    // order, which can be a pause a later `on` has already superseded.
    const latestToggleEvent = data?.pages
      .flatMap(page => page.events)
      .reduce<PropertyUpdateEvent | undefined>((latest, event) => {
        const payload = event.payload as PropertyUpdatePayload | undefined;
        const value = payload?.properties?.ahoy_outbound_enabled;
        if (value !== 'on' && value !== 'off') {
          return latest;
        }
        if (
          !latest ||
          new Date(event.occurred_at).getTime() >
            new Date(latest.occurred_at).getTime()
        ) {
          return event as PropertyUpdateEvent;
        }
        return latest;
      }, undefined);
    if (!latestToggleEvent) {
      return undefined;
    }
    const payload = latestToggleEvent.payload as
      | PropertyUpdatePayload
      | undefined;
    if (payload?.properties?.ahoy_outbound_enabled !== 'off') {
      return undefined;
    }
    return latestToggleEvent.source_type_name === 'ahoy_system'
      ? 'system'
      : 'user';
  }, [data?.pages, isLoading, isError]);
}
